/*global chrome*/

import React, { useEffect, useState } from 'react';
import { useMutation } from '@apollo/client';
import { USER_LOGIN, VERIFY_NON_USER } from '../../graphql/mutation';
import { FingerPrintHash } from '../../../utils/extension';
import { Formik } from 'formik';
import {
    Box,
    Button,
    Checkbox,
    Divider,
    FormControl,
    FormControlLabel,
    FormHelperText,
    InputLabel,
    OutlinedInput,
    Stack,
    Typography,
    useTheme
} from '@mui/material';
import checkboxCheckedIcon from '../../../assets/images/icons/checkbox-checked.svg';
import checkboxIcon from '../../../assets/images/icons/checkbox.svg';
import GoogleLoginButtonNew from 'Components/Auth/GoogleLoginButtonNew';
import LocalStorage from 'utils/extension/localstorage';
import * as Yup from 'yup';
import AnimateButton from 'ui-component/extended/AnimateButton';

const SignIn = ({ setActiveStep, setEmail: setEmailState, loading }) => {
    const isDev = process.env?.REACT_APP_NODE_ENV === 'dev';
    const theme = useTheme();
    const [error, setError] = useState(null);
    const [showPassword, setShowPassword] = useState(false);
    const [toastMessage, setToastMessage] = useState(null);

    const [loginUser] = useMutation(USER_LOGIN);
    const [verifyNonUser] = useMutation(VERIFY_NON_USER);

    const handleGoogleLogin = async (data) => {
        const gData = data.data;
        try {
            const email = gData.userData.user.email;
            if (gData?.otpData) {
                setEmailState(email);
                setActiveStep(1);
            }
            if (gData?.userData) {
                const authToken = gData.userData.token?.accessToken;
                if (gData.userData?.user) {
                    LocalStorage.setUserData({
                        email,
                        firstName: gData.userData.user.firstName,
                        lastName: gData.userData.user.lastName,
                        profileImage: gData.userData.user.profileImage
                    });
                }
                LocalStorage.setAuthToken(authToken);
                LocalStorage.setUserEmail(email);
                setActiveStep(2);
            }
        } catch (error) {
            loading({ primaryLoading: false });
            setError(error.message);
        }
    };

    const onSubmit = async (values) => {
        loading({ primaryLoading: true });
        try {
            await verifyNonUser({
                variables: { email: values.email }
            });
            const fingerHash = await FingerPrintHash();
            const { data } = await loginUser({
                variables: {
                    loginUserInput: {
                        ...values,
                        fingerprint: fingerHash,
                        firebaseToken: null
                    }
                }
            });

            if (data.userLogin?.otpData) {
                setEmailState(values.email);
                setActiveStep(1);
                setToastMessage(data.userLogin.otpData?.message);
            }
            if (data.userLogin?.userData) {
                const authToken = data.userLogin.userData.token?.accessToken;
                if (data.userLogin.userData?.user?.email) {
                    LocalStorage.setUserEmail(data.userLogin.userData.user.email);
                    LocalStorage.setUserData({
                        email: data.userLogin.userData.user.email,
                        firstName: data.userLogin.userData.user.firstName,
                        lastName: data.userLogin.userData.user.lastName,
                        profileImage: data.userLogin.userData.user.profileImage
                    });
                }
                LocalStorage.setAuthToken(authToken);
                setActiveStep(2);
            }
            loading({ primaryLoading: false });
        } catch (error) {
            loading({ primaryLoading: false });
            setError(error.message);
        }
    };

    return (
        <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" gap={2}>
            {!isDev && ( // google login not working on safari..hide until then
                <>
                    <GoogleLoginButtonNew
                        handleChangePage={() => {}}
                        width={326}
                        updateLoading={() => loading({ primaryLoading: true })}
                        handleFailure={(err) => {
                            setError(err);
                            loading({ primaryLoading: false });
                        }}
                        handleSuccess={handleGoogleLogin}
                        disableUserCreation
                    />
                    <Divider sx={{ width: '100%', color: '#00214D', fontSize: 16, fontWeight: 500 }}>or</Divider>
                </>
            )}
            <Typography className="font-roboto">Sign in with email address</Typography>
            <Formik
                initialValues={{
                    email: '',
                    password: ''
                }}
                validationSchema={Yup.object().shape({
                    email: Yup.string()
                        .email('Sorry, but this email address is invalid.')
                        .max(255)
                        .required('Please fill out your email address.'),
                    password: Yup.string().max(255).required('Please fill out your password.')
                })}
                onSubmit={onSubmit}
            >
                {({ errors, handleBlur, handleChange, handleSubmit, isSubmitting, touched, values }) => (
                    <form noValidate onSubmit={handleSubmit} style={{ width: '100%' }}>
                        <FormControl
                            fullWidth
                            error={Boolean(touched.email && errors.email)}
                            sx={{ ...theme.typography.customInput }}
                            className="mt-0 mb-3"
                        >
                            <InputLabel htmlFor="outlined-adornment-email-login">Email address</InputLabel>
                            <OutlinedInput
                                id="outlined-adornment-email-login"
                                type="email"
                                value={values.email}
                                name="email"
                                onBlur={handleBlur}
                                onChange={handleChange}
                                label="Email address"
                                inputProps={{}}
                                autoComplete="username"
                            />
                            {touched.email && errors.email && (
                                <FormHelperText error id="standard-weight-helper-text-email-login">
                                    {errors.email}
                                </FormHelperText>
                            )}
                        </FormControl>
                        <FormControl
                            fullWidth
                            error={Boolean(touched.password && errors.password)}
                            sx={{ ...theme.typography.customInput }}
                            className="mt-0 mb-3"
                        >
                            <InputLabel htmlFor="outlined-adornment-password-login">Password</InputLabel>
                            <OutlinedInput
                                id="outlined-adornment-password-login"
                                type="password"
                                value={values.password}
                                name="password"
                                onBlur={handleBlur}
                                onChange={handleChange}
                                label="Password"
                                inputProps={{}}
                            />
                            {touched.password && errors.password && (
                                <FormHelperText error id="standard-weight-helper-text-password-login">
                                    {errors.password}
                                </FormHelperText>
                            )}
                        </FormControl>
                        <Stack direction="row" justifyContent="space-between">
                            <FormControlLabel
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'row',
                                    gap: 1.5,
                                    alignItems: 'center',
                                    color: '#212121'
                                }}
                                control={
                                    <Checkbox
                                        defaultChecked
                                        icon={<img src={checkboxIcon} className="checkbox-icon" alt="checkbox-icon" />}
                                        checkedIcon={
                                            <img src={checkboxCheckedIcon} className="checkbox-icon" alt="checkbox-checked-icon" />
                                        }
                                    />
                                }
                                label="Remember me"
                            />
                            <Typography className="font-roboto text-dark-blue fw-500">Forgot password?</Typography>
                        </Stack>
                        {error && (
                            <FormHelperText error id="standard-weight-helper-text-email-login">
                                {error}
                            </FormHelperText>
                        )}
                        <Box sx={{ mt: 3 }}>
                            <AnimateButton>
                                <Button
                                    disableElevation
                                    disableRipple
                                    fullWidth
                                    size="large"
                                    type="submit"
                                    variant="contained"
                                    className="alternate"
                                >
                                    Sign in with email
                                </Button>
                            </AnimateButton>
                        </Box>
                    </form>
                )}
            </Formik>
        </Box>
    );
};

export default SignIn;
